/**
 * Paths that should never be used as a post-auth redirect target,
 * to avoid bouncing the user back into the auth flow.
 */
const BLOCKED_REDIRECT_PATHS = new Set<string>([
  "/login",
  "/forgot-password",
  "/reset-password",
  "/password-reset-required",
]);

/**
 * Sanitize a candidate redirect path.
 *
 * Only same-origin relative paths starting with a single "/" are allowed.
 * Rejects absolute URLs, protocol-relative URLs ("//evil.com/..."), and
 * dangerous schemes ("javascript:"). Also rejects known auth pages so we
 * don't loop the user back into the reset flow.
 *
 * Returns the sanitized path, or `fallback` when the candidate is unsafe.
 */
export function sanitizeRedirectPath(
  candidate: unknown,
  fallback: string = "/dashboard",
): string {
  if (typeof candidate !== "string") return fallback;
  const value = candidate.trim();
  if (value.length < 2) return fallback;
  if (value[0] !== "/") return fallback;
  if (value[1] === "/" || value[1] === "\\") return fallback; // protocol-relative
  if (/[\x00-\x1f]/.test(value)) return fallback;

  // Strip query/hash before checking the blocked list.
  const pathnameOnly = value.split(/[?#]/, 1)[0];
  if (BLOCKED_REDIRECT_PATHS.has(pathnameOnly)) return fallback;

  return value;
}
